import React from 'react';
import { Link } from 'react-router-dom';
import { trackCtaClick } from '../analytics/ctaGoal';
import { Helmet } from 'react-helmet-async';
import RouteSeo from '../seo/RouteSeo';
import '../App.css';

type Mark = 'y' | 'p' | 'n';
interface Cell { m: Mark; note?: string; }
// `more` links the feature label to a page of its own. Only the assistant has one;
// the sticky first column is narrow on a phone, so a link per row would turn the
// column people read the table by into a wall of blue. (#800)
interface Row { feat: string; only?: boolean; more?: { to: string; label: string }; cells: Cell[]; } // order: Chaptery, Scrivener, Atticus, Vellum
interface Band { label: string; rows: Row[]; }

const y = (note?: string): Cell => ({ m: 'y', note });
const p = (note?: string): Cell => ({ m: 'p', note });
const n = (note?: string): Cell => ({ m: 'n', note });

const BANDS: Band[] = [
  {
    label: 'Write & plan',
    rows: [
      { feat: 'Draft & organize chapters and scenes', cells: [y(), y(), y(), n()] },
      // Scrivener has Project/Session Targets and a Writing History window, but no
      // streak tracking. Atticus genuinely matches us here — do not mark it down.
      { feat: 'Writing goals, streaks & 30-day history', cells: [y(), p('no streaks'), y(), n()] },
      { feat: 'Storyboard — visual plotting on a canvas', cells: [y(), y(), n(), n()] },
    ],
  },
  {
    label: 'Build your world',
    rows: [
      { feat: 'World builder — characters, locations, events', cells: [y(), p('templates'), n(), n()] },
      { feat: 'Visual relationship graph', only: true, cells: [y(), n(), n(), n()] },
      { feat: 'Reuse one world across a series', only: true, cells: [y(), n(), n(), n()] },
    ],
  },
  {
    label: 'Publish',
    rows: [
      { feat: 'Book design & typesetting', cells: [y(), p(), y(), y()] },
      { feat: 'Print-ready PDF', cells: [y(), p(), y(), y()] },
      { feat: 'EPUB ebook', cells: [y(), y(), y(), y()] },
      { feat: 'Word / DOCX export', cells: [y(), y(), y(), n()] },
    ],
  },
  {
    label: 'Work & sync',
    rows: [
      { feat: 'Native Windows & macOS app', cells: [y(), y(), n('web app'), p('mac only')] },
      { feat: 'Works in your browser too', cells: [y('shelf & tools'), n(), y(), n()] },
      { feat: 'Cloud sync & your Bookshelf', cells: [y(), n('Dropbox'), y(), n()] },
      { feat: 'Revision history', cells: [y(), y(), n(), n()] },
      { feat: 'Free, continuous updates', cells: [y(), n('per version'), y(), y()] },
    ],
  },
  {
    label: 'Smart help',
    rows: [
      { feat: 'AI assistant — automates the work around your book', only: true,
        more: { to: '/features/assistant', label: 'What you can ask it to do' },
        cells: [y('your API key'), n(), n(), n()] },
    ],
  },
];

const MARK_LABEL: Record<Mark, string> = { y: 'Yes', p: 'Limited', n: 'No' };

const CmpMark: React.FC<{ cell: Cell; chap?: boolean }> = ({ cell, chap }) => (
  <>
    <span className={`cmp-mark cmp-mark--${cell.m}${chap ? ' cmp-mark--chap' : ''}`} role="img" aria-label={MARK_LABEL[cell.m]}>
      {cell.m === 'y' ? '✓' : cell.m === 'p' ? '~' : '–'}
    </span>
    {cell.note && <span className="cmp-note">{cell.note}</span>}
  </>
);

const FAQ: { q: string; a: string }[] = [
  {
    q: 'Is Chaptery a good Scrivener alternative?',
    a: 'Yes. Chaptery covers the drafting and outlining Scrivener is known for, then adds what Scrivener leaves out — a structured world builder, a visual relationship graph, built-in cloud sync, revision history, and an AI assistant — all in one app for Windows and macOS.',
  },
  {
    q: 'What is the difference between Chaptery, Atticus, and Vellum?',
    a: 'Atticus and Vellum are formatting tools: you bring a finished manuscript and they turn it into print-ready PDF and EPUB files. Chaptery is an all-in-one writing app that takes you from the first word — drafting, world-building, and planning — all the way to exporting a formatted book.',
  },
  {
    q: 'Can Chaptery format and export finished books?',
    a: 'Yes. Chaptery typesets your manuscript and exports print-ready PDF, EPUB, and Word documents, with design settings for page setup, chapter headings, title pages, and more.',
  },
  {
    q: 'Does Chaptery have an AI writing assistant?',
    a: 'Yes, and it is built for the work around your book — it can see your chapters, so it can build your world from them, and keeping your storyboard in order. It runs on an API key you provide, or on a model you run locally, so you choose the provider and what it costs. None of Scrivener, Atticus, or Vellum includes an AI assistant.',
  },
  {
    q: 'Does Chaptery work across multiple computers?',
    a: 'Yes. Chaptery is a native desktop app for Windows and macOS, and once you sign in your manuscript, world guide, and revisions sync to the cloud so you can move between your computers and pick up where you left off.',
  },
];

const ComparePage = () => (
  <>
  <RouteSeo path="/compare" />
  <Helmet>
    <script type="application/ld+json">{JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQ.map(f => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    })}</script>
    <script type="application/ld+json">{JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Chaptery', item: 'https://trychaptery.com' },
        { '@type': 'ListItem', position: 2, name: 'Compare', item: 'https://trychaptery.com/compare' },
      ],
    })}</script>
  </Helmet>
  <div className="features-page compare-page">

    {/* Hero */}
    <section className="features-hero">
      <div className="container">
        <h1>Chaptery vs. Scrivener, Atticus &amp; Vellum</h1>
        <p className="features-hero-sub">
          If you are comparing novel-writing software — or looking for a Scrivener alternative that also
          handles world-building and publishing — here is how Chaptery stacks up. Most tools do one job:
          drafting, or formatting, or world-building. Chaptery is the all-in-one app that does all three.
        </p>
        <div className="hero-actions">
          <Link to="/register" className="btn-primary-large" onClick={() => trackCtaClick('compare')}>Start Free</Link>
          <Link to="/features" className="btn-secondary-large">See All Features</Link>
        </div>
      </div>
    </section>

    {/* Matrix */}
    <section className="features-split">
      <div className="container">
        <div className="cmp-legend" aria-hidden="true">
          <span><span className="cmp-mark cmp-mark--y">✓</span> Full</span>
          <span><span className="cmp-mark cmp-mark--p">~</span> Limited</span>
          <span><span className="cmp-mark cmp-mark--n">–</span> Not available</span>
        </div>

        {/* aria-hidden: a screen reader reaches every column through the table itself,
            so this would describe a limitation that does not apply to it. Worded for
            scrolling rather than swiping because the query is on width, not pointer —
            at 700px this can be a mouse. */}
        <p className="cmp-swipe-hint" aria-hidden="true">
          Scroll the table sideways to compare Scrivener, Atticus and Vellum →
        </p>

        <div
          className="cmp-scroll"
          tabIndex={0}
          role="region"
          aria-labelledby="cmp-caption"
        >
          <table className="cmp-table">
            <caption className="cmp-caption" id="cmp-caption">Chaptery compared to Scrivener, Atticus, and Vellum</caption>
            <colgroup>
              <col className="cmp-c-feat" />
              <col className="cmp-c-chap" />
              <col className="cmp-c-comp" />
              <col className="cmp-c-comp" />
              <col className="cmp-c-comp" />
            </colgroup>
            <thead>
              <tr>
                <th scope="col" className="cmp-feat"><span className="cmp-sr">Feature</span></th>
                <th scope="col" className="cmp-chap cmp-th-chap">
                  <span className="cmp-badge">All-in-one</span>
                  <span className="cmp-brand">Chaptery</span>
                  <span className="cmp-brand-sub">Write · World · Publish</span>
                </th>
                <th scope="col"><span className="cmp-brand">Scrivener</span><span className="cmp-brand-sub">Drafting</span></th>
                <th scope="col"><span className="cmp-brand">Atticus</span><span className="cmp-brand-sub">Formatting</span></th>
                <th scope="col"><span className="cmp-brand">Vellum</span><span className="cmp-brand-sub">Formatting</span></th>
              </tr>
            </thead>
            <tbody>
              {BANDS.map(band => (
                <React.Fragment key={band.label}>
                  <tr className="cmp-band">
                    <th scope="colgroup" className="cmp-feat cmp-band-label">{band.label}</th>
                    <td className="cmp-chap" />
                    <td /><td /><td />
                  </tr>
                  {band.rows.map(row => (
                    <tr className={`cmp-row${row.only ? ' cmp-row--only' : ''}`} key={row.feat}>
                      <th scope="row" className="cmp-feat">
                        {row.feat}
                        {row.only && <span className="cmp-only-tag">Only Chaptery</span>}
                        {row.more && (
                          <Link className="cmp-feat-more" to={row.more.to}>{row.more.label} →</Link>
                        )}
                      </th>
                      {row.cells.map((cell, i) => (
                        <td key={i} className={i === 0 ? 'cmp-chap' : undefined}>
                          <CmpMark cell={cell} chap={i === 0} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <p className="cmp-fineprint">Competitor details compared from each vendor's site, August 2026. Pricing is on our <Link to="/pricing">pricing page</Link>.</p>
      </div>
    </section>

    {/* Per-competitor breakdown */}
    <section className="features-vs">
      <div className="container">
        <h2>How Chaptery compares, tool by tool</h2>
        <div className="features-vs-grid">
          <div className="features-vs-card">
            <h3>vs. Scrivener</h3>
            <p>
              Scrivener is a superb drafting and outlining tool, but its world-building is folders and
              document templates — there is no visual relationship graph, no built-in cloud sync (it leans
              on Dropbox), and no AI assistant. It is also sold per major version. Chaptery brings the
              structured world builder, the relationship graph, cloud sync, and an assistant into one app.
            </p>
          </div>
          <div className="features-vs-card">
            <h3>vs. Atticus</h3>
            <p>
              Atticus is a browser-based formatting tool with a solid editor and a one-time price — but it
              does no world-building, has no relationship graph, and no AI assistant. Chaptery adds the entire
              pre-writing and world layer Atticus leaves out, and runs as a fast native desktop app.
            </p>
          </div>
          <div className="features-vs-card">
            <h3>vs. Vellum</h3>
            <p>
              Vellum makes beautiful books, but it is Mac-only and formatting-only — no drafting, no
              world-building, and no Word export. Chaptery covers the whole journey from first word to
              finished file, on both Windows and macOS.
            </p>
          </div>
        </div>
      </div>
    </section>

    {/* FAQ */}
    <section className="features-split features-split--alt">
      <div className="container">
        <h2 className="cmp-faq-title">Frequently asked questions</h2>
        <div className="cmp-faq">
          {FAQ.map(f => (
            <div className="cmp-faq-item" key={f.q}>
              <h3>{f.q}</h3>
              <p>{f.a}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="features-cta">
      <div className="container">
        <h2>See why writers are switching to Chaptery</h2>
        <p>One app to write, build your world, and publish. Start free — one book, thirty days, no card required.</p>
        <div className="hero-actions">
          <Link to="/register" className="btn-primary-large" onClick={() => trackCtaClick('compare')}>Start Free</Link>
          <Link to="/pricing" className="btn-secondary-large">View Pricing</Link>
        </div>
      </div>
    </section>

  </div>
  </>
);

export default ComparePage;
